import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { IdCard, FileDown } from "lucide-react";
import { toast } from "sonner";
import { downloadCertificatePdf, downloadBulkCertificatesPdf } from "@/lib/pdf";

const PER_PAGE = 10; // 2 أعمدة × 5 صفوف، مطابق للبرنامج المكتبي

/**
 * SeatNumbersPrint — طباعة استيكرات أرقام جلوس الطلاب، 10 استيكرات في
 * كل صفحة A4، بعدد صفحات يُحسَب تلقائيًا حسب عدد الطلاب. كل استيكر:
 * اسم المدرسة + شارتا المرحلة/الصف، ثم اسم الطالب ورقم الجلوس البارز
 * والقسم — بنفس تصميم البرنامج المكتبي.
 */
export default function SeatNumbersPrint() {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];
  const [grade, setGrade] = useState("");
  const [cls, setCls] = useState("__all__");
  const [classes, setClasses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!grade) { setClasses([]); return; }
    api.get("/students/classes", { params: { stage: ctx.stage, year: ctx.year, grade } })
      .then(({ data }) => setClasses(data)).catch(() => {});
  }, [ctx.stage, ctx.year, grade]);

  const generate = async () => {
    if (!grade) return toast.error("اختر الصف أولاً");
    setGenerating(true);
    try {
      const { data: students } = await api.get("/students", {
        params: {
          stage: ctx.stage, year: ctx.year, grade,
          ...(cls !== "__all__" ? { class_name: cls } : {}),
        },
      });
      const sorted = [...students].sort((a, b) =>
        (parseInt(a.seat_number, 10) || 0) - (parseInt(b.seat_number, 10) || 0));

      if (!sorted.length) {
        toast.error("لا يوجد طلاب في هذا التحديد");
        return;
      }

      const container = document.createElement("div");
      container.setAttribute("dir", "rtl");
      container.style.position = "fixed";
      container.style.left = "-99999px";
      container.style.top = "0";
      container.style.direction = "rtl";
      container.style.background = "#ffffff";
      document.body.appendChild(container);

      const { createRoot } = await import("react-dom/client");
      const nodes = [];
      const pageCount = Math.ceil(sorted.length / PER_PAGE);
      for (let p = 0; p < pageCount; p++) {
        const pageStudents = sorted.slice(p * PER_PAGE, p * PER_PAGE + PER_PAGE);
        const wrap = document.createElement("div");
        wrap.style.width = "794px";
        wrap.style.padding = "16px";
        wrap.style.background = "#ffffff";
        container.appendChild(wrap);
        const root = createRoot(wrap);
        await new Promise((resolve) => {
          root.render(
            <StickerPage school={settings} stage={ctx.stage} grade={grade}
                          students={pageStudents} />
          );
          document.fonts.ready.then(() => setTimeout(resolve, 150));
        });
        nodes.push(wrap);
      }

      const label = `${grade}-${cls === "__all__" ? "الكل" : cls}`;
      if (nodes.length === 1) {
        await downloadCertificatePdf(nodes[0], `أرقام-الجلوس-${label}.pdf`);
      } else {
        await downloadBulkCertificatesPdf(nodes, `أرقام-الجلوس-${label}.pdf`);
      }
      container.remove();
      toast.success("تم توليد ملف أرقام الجلوس بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="seatnumbers-page">
      <PageHeader
        icon={IdCard}
        overline="التقارير والشهادات"
        title="طباعة أرقام الجلوس"
        subtitle="استيكرات أرقام جلوس الطلاب — 10 في كل صفحة A4، جاهزة للطباعة والقص."
      />

      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[12px]">الصف</Label>
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger className="h-10" data-testid="sn-grade"><SelectValue placeholder="اختر الصف" /></SelectTrigger>
            <SelectContent>
              {grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[12px]">الفصل</Label>
          <Select value={cls} onValueChange={setCls} disabled={!grade}>
            <SelectTrigger className="h-10" data-testid="sn-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">كل الفصول</SelectItem>
              {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={generate} disabled={!grade || generating} className="h-10 w-full" data-testid="sn-generate">
            <FileDown className="h-4 w-4 me-1.5" /> {generating ? "جارٍ التوليد…" : "تنزيل PDF"}
          </Button>
        </div>
      </div>

      {!grade && (
        <div className="rounded-xl border border-dashed border-border bg-white p-16 text-center text-muted-foreground">
          اختر الصف لعرض خيارات الطباعة
        </div>
      )}
    </div>
  );
}

/** صفحة واحدة فيها 10 استيكرات (2×5) — للطباعة/التصدير فقط. */
function StickerPage({ school, stage, grade, students }) {
  const cells = [...students];
  while (cells.length < 10) cells.push(null); // خلايا فارغة لإكمال الشبكة بصريًا
  return (
    <div className="grid grid-cols-2 gap-4">
      {cells.map((s, i) => (
        <StickerCard key={i} school={school} stage={stage} grade={grade} student={s} />
      ))}
    </div>
  );
}

function StickerCard({ school, stage, grade, student }) {
  if (!student) return <div style={{ minHeight: "168px" }} />;
  const cell = { border: "none", padding: 0, margin: 0, verticalAlign: "middle" };
  return (
    <table style={{ width: "100%", height: "168px", borderCollapse: "collapse", border: "2px solid #0F5257", borderRadius: "12px", overflow: "hidden", background: "#fff" }}>
      <tbody>
        <tr>
          {/* الشريط الذهبي أولًا في الكود = أقصى اليسار بصريًا (ترتيب
              فعلي مباشر، بلا اعتماد على أي تفسير تلقائي للاتجاه). */}
          <td style={{ ...cell, width: "6px", background: "#C9A227" }} />
          <td style={{ ...cell, padding: "12px" }}>

            <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
              <tr>
                <td style={{ border: "none", padding: 0, textAlign: "center" }}>
                  <div className="font-bold leading-snug" style={{ fontSize: "11px", color: "#0F5257" }}>{school?.school_name || "المدرسة"}</div>
                  {/* شارتا المرحلة والصف داخل جدول فرعي بثلاث خلايا
                      (شارة، فاصلة، شارة)، كل خلية بمحاذاة رأسية وسط
                      حقيقية عبر vertical-align — الطريقة الأثبت عبر كل
                      محركات العرض تمامًا، بلا أي اعتماد على flexbox. */}
                  <table style={{ borderCollapse: "collapse", margin: "6px auto 0" }}><tbody>
                    <tr>
                      <td style={{ border: "none", padding: 0, verticalAlign: "middle" }}>
                        <span className="font-bold whitespace-nowrap" style={{ background: "#0F5257", color: "#fff", fontSize: "8.5px", borderRadius: "999px", padding: "4px 8px", display: "inline-block" }}>{grade}</span>
                      </td>
                      <td style={{ border: "none", padding: "0 6px", verticalAlign: "middle" }}>
                        <span className="font-bold" style={{ color: "#0F5257", fontSize: "9px" }}>•</span>
                      </td>
                      <td style={{ border: "none", padding: 0, verticalAlign: "middle" }}>
                        <span className="font-bold whitespace-nowrap" style={{ background: "#0F5257", color: "#fff", fontSize: "8.5px", borderRadius: "999px", padding: "4px 8px", display: "inline-block" }}>{stage}</span>
                      </td>
                    </tr>
                  </tbody></table>
                </td>
              </tr>
              <tr>
                <td style={{ border: "none", padding: 0, borderTop: "1px solid #CBD5E1", paddingTop: "10px" }}>

                  <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}><tbody>
                    <tr>
                      {/* الشعار أولًا في الكود = أقصى اليسار */}
                      <td style={{ border: "none", padding: 0, width: "26%", verticalAlign: "middle", textAlign: "center" }}>
                        {school?.school_logo ? (
                          <img src={school.school_logo} alt="شعار" style={{ maxWidth: "100%", maxHeight: "56px", objectFit: "contain", display: "inline-block" }} crossOrigin="anonymous" />
                        ) : (
                          <IdCard className="h-7 w-7 text-slate-300" style={{ display: "inline-block" }} />
                        )}
                      </td>
                      {/* بيانات الطالب ثانيًا = يمين الشعار، محاذاة نص
                          يمين فيزيائية صريحة (لا خصائص منطقية إطلاقًا) */}
                      <td style={{ border: "none", padding: 0, verticalAlign: "middle", textAlign: "right", borderRight: "1px solid #E2E8F0", paddingRight: "8px" }}>
                        <div style={{ fontSize: "8.5px", color: "#64748B" }}>اسم الطالب</div>
                        <div className="font-bold leading-snug break-words" style={{ fontSize: "12px", marginTop: "2px" }}>{student.name}</div>

                        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}><tbody>
                          <tr>
                            <td style={{ border: "none", padding: 0, verticalAlign: "middle", fontSize: "8px", color: "#334155" }}>
                              <div className="whitespace-nowrap">الصف : {grade}</div>
                              <div className="whitespace-nowrap" style={{ marginTop: "4px" }}>القسم : {student.section || "—"}</div>
                            </td>
                            <td style={{ border: "none", padding: 0, verticalAlign: "middle", textAlign: "left", width: "1%" }}>
                              <table style={{ borderCollapse: "collapse", border: "2px solid #C9A227", borderRadius: "6px" }}><tbody>
                                <tr>
                                  <td style={{ border: "none", padding: "6px 10px", verticalAlign: "middle", textAlign: "center" }}>
                                    <span className="font-bold tabular-nums" style={{ fontSize: "15px" }}>{student.seat_number || "—"}</span>
                                  </td>
                                </tr>
                              </tbody></table>
                            </td>
                          </tr>
                        </tbody></table>
                      </td>
                    </tr>
                  </tbody></table>

                </td>
              </tr>
            </tbody></table>

          </td>
        </tr>
      </tbody>
    </table>
  );
}
